// 2) Third-party library imports
import { forEach, get, includes } from 'lodash-es';
import { all, call, delay, fork, put, select, takeLatest } from 'redux-saga/effects';
import { verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';

// 3a) Constants
import config, { configAmplify, generateAmplifyConfig } from '../../AmplifyConfig';
import { API_NAME, DEFAULT_REGION_NAME, maxAPIRefetchCount, refetchAPIDelay } from '../../config/config';

// 3d) Utility functions
import { DynamicObject } from '../../utils/commonInterfaces';
import { checkShouldRequestRefetch, getRegionConfigFromList } from '../../utils/commonFunctions';
import { post } from 'src/api';

// 3e) Redux actions & types
import { getCurrentUser } from '../auth/authSlice';
import {
    changeEndpointsAction,
    generateMfaSoftwareTokenAction,
    getRegionKeyConfigAction,
    getRegionSettingsConfigAction,
    useSmsMfaAction,
    verifyMfaSoftwareTokenAction,
    updateRegionSettingsConfigAction,
    updateRegionKeyConfigAction,
    getRegionKeyConfig,
    getRegionSettingsConfig,
    getIdentityProviderConfigAction,
    updateIdentityProviderConfigAction,
} from './authSlice';

/**
 * Function called for changing the endpoints (multi-region setup).
 */
function* handleChangeEndpoints({ payload: sagaPayload }: any) {
    try {
        const regionKeyConfig: DynamicObject[] = yield select(getRegionKeyConfig);
        const regionSettingsConfig: DynamicObject[] = yield select(getRegionSettingsConfig);
        const config = getRegionConfigFromList(
            get(sagaPayload, 'Company.Region', DEFAULT_REGION_NAME),
            regionKeyConfig,
            regionSettingsConfig
        );

        const amplifyConfig: DynamicObject = yield generateAmplifyConfig(config);
        yield call(configAmplify, amplifyConfig);

        // To call async functions, use redux-saga's `call()`.
        yield call(post, {
            apiName: API_NAME,
            path: '/core/activatesystems'
        });

        return;
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

let refetchCount = 0;

/**
 * Function called for getting the region key config from the json file.
 */
function* handleGetRegionSettingsConfig() {
    try {
        const res: DynamicObject = yield call(
            fetch,
            `${get(config, `API.REST.${API_NAME}.endpoint`)}/region`
        );

        const responseData: DynamicObject = yield res.json();
        const regionSettingConfig = get(responseData, 'Settings');
        refetchCount = 0;

        yield put(updateRegionSettingsConfigAction(regionSettingConfig));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getRegionSettingsConfigAction());
        } else {
            console.log('Error trying to get region settings', err);
        }
    }
}

/**
 * Function called for getting the region key config from the json file.
 */
function* handleGetRegionKeyConfig() {
    try {
        const jsonResponse: DynamicObject = yield call(
            fetch,
            // `${get(config, 'API.endpoints.0.endpoint')}/multi-region-config.json`
            `${window.location.origin}/multi-region-config.json`
        );

        const regionKeyConfig: DynamicObject[] = yield jsonResponse.json();
        refetchCount = 0;

        yield put(updateRegionKeyConfigAction(regionKeyConfig));
    } catch (err) {
        console.log('err: ', err);

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getRegionKeyConfigAction());
        } else {
            console.log('Error trying to get region key config', err);
        }
    }
}


function* handleGenerateMfaSoftwareToken({ payload: sagaPayload }: any) {
    const { callback } = sagaPayload;

    try {
        // To call async functions, use redux-saga's `call()`.
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/mfa/generate/software/token'
        });

        if (callback) {
            const res: DynamicObject = yield body.json();
            const response = {
                IsSuccess: true,
                QrCode: get(res, 'QrCode')
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData: any;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
        //if (callback) {
        //    const returnData = get(err.response, 'data')
        //        ? err.response.data
        //        : { Messages: [err.message] };
        //    returnData.IsSuccess = false;
        //    callback(returnData);
        //}
        //if (err instanceof Error) {
        //    console.log('Error', err);
        //} else {
        //    console.error('An unknown error occured.');
        //}
    }
}

//function* handleSignInWithOtherProviders({ payload: sagaPayload }: any) {
//    const { provider } = sagaPayload;
//    const credentials: DynamicObject = yield Auth.federatedSignIn({ customProvider: provider });
//    yield credentials;
//}

function* handleVerifyMfaSoftwareToken({ payload: sagaPayload }: any) {
    const { callback, payload } = sagaPayload;
    const UserCode = get(payload, 'UserCode');

    const handleError = (err: any) => {
        if (callback) {
            const returnData = get(err.response, 'data')
                ? err.response.data
                : { Messages: [err.message] };
            const Messages = get(returnData, 'Messages');

            forEach (Messages, (message, idx) => {
                if (includes(message, 'Code mismatch')) {
                    Messages[idx] = 'Invalid OTP code';
                }
            });

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }

    try {
        const currentUser: DynamicObject = yield select(getCurrentUser);
        // To call async functions, use redux-saga's `call()`.
        //yield call(Auth.verifyTotpToken, currentUser, UserCode);
        yield call(verifyTOTPSetup, { code: UserCode });

        // Call setPreferredMFA using call effect
        // const preferredMFA: string = yield call([Auth, 'setPreferredMFA'], currentUser, 'TOTP');
        yield call(updateMFAPreference, { totp: 'PREFERRED' });

        //const newUserData = update(currentUser, {
        //    $merge: {
        //        preferredMFA
        //    },
        //});
        // const newUserData = {
        //     ...currentUser,
        //     preferredMFA: 'TOTP',
        // };

        // yield put(loginUserAction({
        //     currentUser: newUserData,
        //     isAuth: true,
        // }));

        if (callback) {
            callback({ IsSuccess: true });
        }
    } catch (err) {
        handleError(err);
    }
}

function* handleUseSmsMfa({ payload }: any) {
    const { callback } = payload;

    const handleError = (err: any) => {
        if (callback) {
            const returnData = get(err.response, 'data')
                ? err.response.data
                : { Messages: [err.message] };
            const Messages = get(returnData, 'Messages');

            forEach (Messages, (message, idx) => {
                if (includes(message, 'Code mismatch')) {
                    Messages[idx] = 'Invalid OTP code';
                }
            });

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }

    try {
        yield call(updateMFAPreference, { sms: 'PREFERRED' });

        if (callback) {
            callback({ IsSuccess: true });
        }
    } catch (err) {
        handleError(err);
    }
}

function * handleGetIdentityProviderConfig() {
    try {
        const jsonResponse: DynamicObject = yield call(
            fetch,
            `${window.location.origin}/identity-provider-config.json`
        );
        const identityProviderConfig: DynamicObject[] = yield jsonResponse.json();

        refetchCount = 0;
        yield put(updateIdentityProviderConfigAction(identityProviderConfig));

   } catch (err) {
        console.log('err: ', err);

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getIdentityProviderConfigAction());
        } else {
            console.log('Error trying to get region key config', err);
        }
   }
}

function* watchGetIdentityProviderConfig() {
    yield takeLatest(
        getIdentityProviderConfigAction.type,
        handleGetIdentityProviderConfig
    );
}

function* watchChangeEndpoints() {
    yield takeLatest(changeEndpointsAction.type, handleChangeEndpoints);
}

function* watchGetRegionSettingsConfig() {
    yield takeLatest(
        getRegionSettingsConfigAction.type,
        handleGetRegionSettingsConfig
    );
}

function* watchGetRegionKeyConfig() {
    yield takeLatest(
        getRegionKeyConfigAction.type,
        handleGetRegionKeyConfig
    );
}

function* watchGenerateMfaSoftwareToken() {
    yield takeLatest(
        generateMfaSoftwareTokenAction.type,
        handleGenerateMfaSoftwareToken
    );
}

function* watchVerifyMfaSoftwareToken() {
    yield takeLatest(
        verifyMfaSoftwareTokenAction.type,
        handleVerifyMfaSoftwareToken
    );
}

//function* watchSignInWithOtherProviders() {
//    yield takeLatest(
//        AuthActionTypes.type,
//        handleSignInWithOtherProviders
//    );
//}

function* watchUseSmsMfa() {
    yield takeLatest(
        useSmsMfaAction.type,
        handleUseSmsMfa
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* authSaga() {
    yield all([
        fork(watchChangeEndpoints),
        fork(watchGetRegionSettingsConfig),
        fork(watchGetRegionKeyConfig),
        fork(watchGenerateMfaSoftwareToken),
        fork(watchVerifyMfaSoftwareToken),
        fork(watchUseSmsMfa),
        //fork(watchSignInWithOtherProviders),
        fork(watchGetIdentityProviderConfig)
    ]);
}

export default authSaga;